/**
 * 引流策略相关API接口
 * 为用户引流功能预留后端接口调用
 */

import { http } from '@/utils/http';
import type { ApiResponse } from '@/types/apiTypes';
import type { AxiosResponse } from 'axios';

// 用户使用统计接口
export interface UsageStats {
  dailyUsed: number;
  dailyLimit: number;
  monthlyUsed?: number;
  monthlyLimit?: number;
  totalUsed: number;
  usagePercentage: number;
  resetTime: string;
}

// 引流活动配置
export interface LeadCampaign {
  id: string;
  name: string;
  type: 'register' | 'upgrade' | 'referral';
  title: string;
  description: string;
  benefits: string[];
  ctaText: string;
  ctaUrl: string;
  isActive: boolean;
  targetAudience: 'anonymous' | 'registered' | 'free' | 'paid';
  triggerConditions: {
    usagePercentage?: number;
    daysSinceRegistration?: number;
    featureUsage?: string[];
  };
  displayConfig: {
    position: 'modal' | 'banner' | 'sidebar' | 'inline';
    priority: number;
    maxDisplays: number;
    cooldownHours: number;
  };
}

// 用户行为跟踪
export interface UserBehavior {
  userId?: string;
  sessionId: string;
  eventType: string;
  eventData: Record<string, any>;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
}

// 转化漏斗数据
export interface ConversionFunnel {
  stage: string;
  usersCount: number;
  conversionRate: number;
  dropOffRate: number;
  avgTimeInStage: number;
}

/**
 * 获取用户使用统计
 */
export const getUserUsageStats = async (): Promise<AxiosResponse<ApiResponse<UsageStats>>> => {
  return http.get('/api/v1/stats/usage');
};

/**
 * 获取免费用户使用统计（基于IP+指纹）
 */
export const getFreeUserUsageStats = async (params: {
  ipAddress?: string;
  fingerprint?: string;
}): Promise<AxiosResponse<ApiResponse<UsageStats>>> => {
  return http.get('/api/v1/stats/free-usage', { params });
};

/**
 * 获取活跃的引流活动
 */
export const getActiveLeadCampaigns = async (params: {
  userType: 'anonymous' | 'registered' | 'free' | 'paid';
  usagePercentage?: number;
}): Promise<AxiosResponse<ApiResponse<LeadCampaign[]>>> => {
  return http.get('/api/v1/lead/campaigns', { params });
};

/**
 * 记录引流活动展示
 */
export const recordCampaignDisplay = async (data: {
  campaignId: string;
  userId?: string;
  sessionId: string;
  displayPosition: string;
}): Promise<AxiosResponse<ApiResponse<void>>> => {
  return http.post('/api/v1/lead/campaigns/display', data);
};

/**
 * 记录引流活动点击
 */
export const recordCampaignClick = async (data: {
  campaignId: string;
  userId?: string;
  sessionId: string;
  actionType: 'cta_click' | 'dismiss' | 'close';
}): Promise<AxiosResponse<ApiResponse<void>>> => {
  return http.post('/api/v1/lead/campaigns/click', data);
};

/**
 * 跟踪用户行为
 */
export const trackUserBehavior = async (data: UserBehavior): Promise<AxiosResponse<ApiResponse<void>>> => {
  return http.post('/api/v1/analytics/behavior', data);
};

/**
 * 批量用户行为跟踪
 */
export const batchTrackBehavior = async (data: UserBehavior[]): Promise<AxiosResponse<ApiResponse<void>>> => {
  return http.post('/api/v1/analytics/behavior/batch', { behaviors: data });
};

/**
 * 获取转化漏斗数据
 */
export const getConversionFunnel = async (params: {
  dateRange: string;
  funnelType: 'registration' | 'upgrade' | 'usage';
}): Promise<AxiosResponse<ApiResponse<ConversionFunnel[]>>> => {
  return http.get('/api/v1/analytics/funnel', { params });
};

/**
 * 检查用户是否符合特定引流条件
 */
export const checkLeadEligibility = async (params: {
  userId?: string;
  checkType: 'register_prompt' | 'upgrade_prompt' | 'feature_unlock';
  context?: Record<string, any>;
}): Promise<AxiosResponse<ApiResponse<{
  eligible: boolean;
  reason?: string;
  recommendedAction?: string;
  campaignId?: string;
}>>> => {
  return http.get('/api/v1/lead/eligibility', { params });
};

/**
 * 获取个性化推荐套餐
 */
export const getRecommendedPackages = async (params: {
  userId?: string;
  usagePattern?: 'light' | 'medium' | 'heavy';
  featurePreferences?: string[];
}): Promise<AxiosResponse<ApiResponse<{
  recommendedPackageId: number;
  reason: string;
  comparisonPackages: number[];
  discountAvailable?: {
    type: 'percentage' | 'fixed';
    value: number;
    expiresAt: string;
  };
}>>> => {
  return http.get('/api/v1/packages/recommendations', { params });
};

/**
 * 记录引流转化事件
 */
export const recordConversionEvent = async (data: {
  eventType: 'registration' | 'package_purchase' | 'feature_usage';
  userId?: string;
  sessionId: string;
  sourceCampaignId?: string;
  conversionValue?: number;
  metadata?: Record<string, any>;
}): Promise<AxiosResponse<ApiResponse<void>>> => {
  return http.post('/api/v1/analytics/conversion', data);
};

/**
 * 获取A/B测试配置
 */
export const getABTestConfig = async (params: {
  testName: string;
  userId?: string;
  sessionId: string;
}): Promise<AxiosResponse<ApiResponse<{
  variant: string;
  config: Record<string, any>;
  trackingId: string;
}>>> => {
  return http.get('/api/v1/ab-test/config', { params });
};

/**
 * 记录A/B测试事件
 */
export const recordABTestEvent = async (data: {
  trackingId: string;
  eventType: string;
  eventData?: Record<string, any>;
}): Promise<AxiosResponse<ApiResponse<void>>> => {
  return http.post('/api/v1/ab-test/event', data);
};

// 导出所有接口
export const leadStrategyApi = {
  getUserUsageStats,
  getFreeUserUsageStats,
  getActiveLeadCampaigns,
  recordCampaignDisplay,
  recordCampaignClick,
  trackUserBehavior,
  batchTrackBehavior,
  getConversionFunnel,
  checkLeadEligibility,
  getRecommendedPackages,
  recordConversionEvent,
  getABTestConfig,
  recordABTestEvent
};

export default leadStrategyApi;
